import React, { useEffect, useState } from 'react';
import { FaPlus, FaTrash, FaEdit, FaTimes } from 'react-icons/fa';
import {
  getProjectItemUpdates,
  createProjectItemUpdate,
  updateProjectItemUpdate,
  deleteProjectItemUpdate,
  ProjectItemUpdate
} from '../../api/itemUpdates';
import { toast } from 'react-toastify';

interface ItemUpdatesModalProps {
  projectId: string;
  itemId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ItemUpdatesModal: React.FC<ItemUpdatesModalProps> = ({
  projectId,
  itemId,
  isOpen,
  onClose
}) => {
  const [updates, setUpdates] = useState<ProjectItemUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProjectItemUpdate>({
    item_id: itemId,
    description: '',
    completion_delta: 0,
    previous_status: '',
    new_status: '',
    blockers: '',
    hours_logged: 0
  });

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getProjectItemUpdates(projectId, itemId);
        setUpdates(Array.isArray(data) ? data : []);
      } catch (e: any) {
        toast.error(e.message || 'Failed to load updates');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, projectId, itemId]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      item_id: itemId,
      description: '',
      completion_delta: 0,
      previous_status: '',
      new_status: '',
      blockers: '',
      hours_logged: 0
    });
  };

  const handleSubmit = async () => {
    if (!form.description?.trim()) {
      toast.error('Description is required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const updated = await updateProjectItemUpdate(projectId, itemId, editingId, {
          ...form,
          item_id: itemId
        });
        setUpdates(prev => prev.map(u => (u.id === editingId ? updated : u)));
        toast.success('Update saved');
      } else {
        const created = await createProjectItemUpdate(projectId, itemId, {
          ...form,
          item_id: itemId
        });
        setUpdates(prev => [created, ...prev]);
        toast.success('Update added');
      }
      resetForm();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save update');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (update: ProjectItemUpdate) => {
    setEditingId(update.id || null);
    setForm({
      item_id: itemId,
      description: update.description || '',
      completion_delta: update.completion_delta ?? 0,
      previous_status: update.previous_status || '',
      new_status: update.new_status || '',
      blockers: update.blockers || '',
      hours_logged: update.hours_logged ?? 0
    });
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    try {
      await deleteProjectItemUpdate(projectId, itemId, id);
      setUpdates(prev => prev.filter(u => u.id !== id));
      if (editingId === id) resetForm();
      toast.success('Update deleted');
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete update');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-[#eaeaea] max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#eaeaea]">
          <h3 className="text-lg font-semibold">Updates</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#444]">
            <FaTimes />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#444] mb-1">Description *</label>
              <textarea
                className="w-full border border-[#eaeaea] rounded-lg px-3 py-2"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">Completion Δ</label>
              <input
                type="number"
                className="w-full border border-[#eaeaea] rounded-lg px-3 py-2"
                value={form.completion_delta ?? 0}
                onChange={(e) => setForm({ ...form, completion_delta: Number(e.target.value) })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">Hours logged</label>
              <input
                type="number"
                className="w-full border border-[#eaeaea] rounded-lg px-3 py-2"
                value={form.hours_logged ?? 0}
                onChange={(e) => setForm({ ...form, hours_logged: Number(e.target.value) })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">Previous status</label>
              <input
                className="w-full border border-[#eaeaea] rounded-lg px-3 py-2"
                value={form.previous_status}
                onChange={(e) => setForm({ ...form, previous_status: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">New status</label>
              <input
                className="w-full border border-[#eaeaea] rounded-lg px-3 py-2"
                value={form.new_status}
                onChange={(e) => setForm({ ...form, new_status: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#444] mb-1">Blockers</label>
              <textarea
                className="w-full border border-[#eaeaea] rounded-lg px-3 py-2"
                rows={2}
                value={form.blockers}
                onChange={(e) => setForm({ ...form, blockers: e.target.value })}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            {editingId && (
              <button
                onClick={resetForm}
                className="px-3 py-2 border border-[#eaeaea] rounded-lg text-[#444] hover:bg-[#fafafa]"
                disabled={saving}
              >
                Cancel edit
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId ? 'Save update' : 'Add update'}
            </button>
          </div>

          <div className="border-t border-[#eaeaea] pt-3">
            <div className="flex items-center gap-2 mb-2">
              <FaPlus className="text-[#aaa]" />
              <span className="text-sm text-[#555]">Updates history</span>
            </div>
            {loading ? (
              <div className="text-sm text-[#888]">Loading...</div>
            ) : !Array.isArray(updates) || updates.length === 0 ? (
              <div className="text-sm text-[#888]">No updates yet</div>
            ) : (
              <div className="space-y-3">
                {updates.map((u) => (
                  <div key={u.id} className="border border-[#eaeaea] rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <p className="text-sm text-[#171717] whitespace-pre-line">{u.description}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(u)}
                          className="text-xs text-[#171717] hover:text-[#333] flex items-center gap-1"
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(u.id)}
                          className="text-xs text-[#888] hover:text-[#dc2626] flex items-center gap-1"
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-[#888] mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {u.completion_delta !== undefined && <span>Δ: {u.completion_delta}</span>}
                      {u.hours_logged !== undefined && <span>Hours: {u.hours_logged}</span>}
                      {u.previous_status && <span>Prev: {u.previous_status}</span>}
                      {u.new_status && <span>New: {u.new_status}</span>}
                      {u.blockers && <span className="col-span-2">Blockers: {u.blockers}</span>}
                      {u.created_at && <span>Created: {new Date(u.created_at).toLocaleString()}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

